import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  Workspace,
  CreateWorkspaceDto,
  UpdateWorkspaceDto,
  AddWorkspaceMemberDto,
  WorkspaceMember,
  WorkspaceMemberRole,
  Board,
  WorkspaceVisibility,
} from '../types/common';
import { workspacesApi } from '../api/endpoints/workspacesApi';
import { createBoard, updateBoard, deleteBoard } from './boardSlice';

interface WorkspaceState {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  workspaceBoards: Board[];
  loading: boolean;
  error: string | null;
  searchQuery: string;
  filteredWorkspaces: Workspace[];
}

const initialState: WorkspaceState = {
  workspaces: [],
  currentWorkspace: null,
  workspaceBoards: [],
  loading: false,
  error: null,
  searchQuery: '',
  filteredWorkspaces: [],
};

// Helper function to map frontend visibility to backend visibility
const mapVisibilityToBackend = (
  visibility: WorkspaceVisibility | string
): 'public' | 'private' => {
  const visStr = String(visibility);
  if (visStr === 'open' || visStr === 'public') {
    return 'public';
  }
  return 'private';
};

// Helper function to map backend visibility to frontend visibility
const mapVisibilityToFrontend = (
  visibility: 'public' | 'private'
): WorkspaceVisibility => {
  return visibility === 'public' ? ('open' as WorkspaceVisibility) : ('closed' as WorkspaceVisibility);
};

// Async thunks
export const fetchWorkspaces = createAsyncThunk('workspaces/fetchWorkspaces', async () => {
  const response = await workspacesApi.getWorkspaces();
  // Map backend visibility to frontend visibility
  const workspaces = response.workspaces.map((ws: any) => ({
    ...ws,
    visibility: mapVisibilityToFrontend(ws.visibility as unknown as 'public' | 'private'),
    color: ws.color || '#0073ea', // Add default color if not present
  }));
  return workspaces;
});

export const fetchWorkspace = createAsyncThunk(
  'workspaces/fetchWorkspace',
  async (workspaceId: string) => {
    const response = await workspacesApi.getWorkspace(workspaceId);
    // Map backend visibility to frontend visibility
    const backendWorkspace: any = response.workspace;
    if (!backendWorkspace) {
      throw new Error('Workspace not found');
    }
    const backendVisibility: any = backendWorkspace.visibility || 'private';
    const workspace = {
      ...backendWorkspace,
      visibility: mapVisibilityToFrontend(backendVisibility as 'public' | 'private'),
      color: backendWorkspace.color || '#0073ea',
      members: backendWorkspace.members || [],
      boardCount: backendWorkspace.boards?.length || 0,
      memberCount: backendWorkspace.members?.length || 0,
    };
    return workspace;
  }
);

export const createWorkspace = createAsyncThunk(
  'workspaces/createWorkspace',
  async (data: CreateWorkspaceDto) => {
    // Transform frontend data to backend format
    const backendData: any = {
      name: data.name,
      description: data.description,
      visibility: mapVisibilityToBackend(data.visibility || 'closed'),
    };
    const response = await workspacesApi.createWorkspace(backendData);

    // Map backend visibility to frontend and add color
    const backendWorkspace: any = response.workspace || response;
    const backendVisibility: any = backendWorkspace.visibility || 'private';
    const workspace = {
      ...backendWorkspace,
      visibility: mapVisibilityToFrontend(backendVisibility as 'public' | 'private'),
      color: data.color || '#0073ea', // Store color from frontend request
      members: backendWorkspace.members || [],
      boardCount: backendWorkspace.boards?.length || 0,
      memberCount: backendWorkspace.members?.length || 0,
    };
    return workspace;
  }
);

export const updateWorkspace = createAsyncThunk(
  'workspaces/updateWorkspace',
  async ({ workspaceId, data }: { workspaceId: string; data: UpdateWorkspaceDto }) => {
    // Transform frontend data to backend format
    const backendData: any = {
      name: data.name,
      description: data.description,
    };
    if (data.visibility) {
      backendData.visibility = mapVisibilityToBackend(data.visibility);
    }
    const response = await workspacesApi.updateWorkspace(workspaceId, backendData);
    // Map backend visibility to frontend and preserve color
    const backendWorkspace: any = response.workspace;
    const backendVisibility: any = backendWorkspace.visibility;
    const workspace = {
      ...backendWorkspace,
      visibility: mapVisibilityToFrontend(backendVisibility as 'public' | 'private'),
      color: data.color || backendWorkspace.color || '#0073ea',
    };
    return workspace;
  }
);

export const deleteWorkspace = createAsyncThunk(
  'workspaces/deleteWorkspace',
  async (workspaceId: string) => {
    await workspacesApi.deleteWorkspace(workspaceId);
    return workspaceId;
  }
);

export const fetchWorkspaceBoards = createAsyncThunk(
  'workspaces/fetchWorkspaceBoards',
  async (workspaceId: string) => {
    const response = await workspacesApi.getWorkspaceBoards(workspaceId);
    return response.boards;
  }
);

export const addWorkspaceMember = createAsyncThunk(
  'workspaces/addMember',
  async (
    { workspaceId, data }: { workspaceId: string; data: AddWorkspaceMemberDto },
    { rejectWithValue }
  ) => {
    try {
      const response = await workspacesApi.addMember(workspaceId, data);
      return response.member;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add member');
    }
  }
);

export const removeWorkspaceMember = createAsyncThunk(
  'workspaces/removeMember',
  async (
    { workspaceId, userId }: { workspaceId: string; userId: string },
    { rejectWithValue }
  ) => {
    try {
      await workspacesApi.removeMember(workspaceId, userId);
      return userId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to remove member');
    }
  }
);

export const updateMemberRole = createAsyncThunk(
  'workspaces/updateMemberRole',
  async (
    {
      workspaceId,
      userId,
      role,
    }: {
      workspaceId: string;
      userId: string;
      role: WorkspaceMemberRole;
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await workspacesApi.updateMemberRole(workspaceId, userId, role);
      return response.member;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update member role');
    }
  }
);

export const searchWorkspaces = createAsyncThunk(
  'workspaces/searchWorkspaces',
  async (query: string) => {
    const response = await workspacesApi.searchWorkspaces(query);
    return response.workspaces;
  }
);

const workspaceSlice = createSlice({
  name: 'workspaces',
  initialState,
  reducers: {
    setCurrentWorkspace: (state, action: PayloadAction<Workspace | null>) => {
      state.currentWorkspace = action.payload;
    },
    clearCurrentWorkspace: (state) => {
      state.currentWorkspace = null;
      state.workspaceBoards = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload;
      if (action.payload === '') {
        state.filteredWorkspaces = state.workspaces;
      } else {
        const query = action.payload.toLowerCase();
        state.filteredWorkspaces = state.workspaces.filter(
          (workspace) =>
            workspace.name.toLowerCase().includes(query) ||
            workspace.description?.toLowerCase().includes(query)
        );
      }
    },
    clearSearch: (state) => {
      state.searchQuery = '';
      state.filteredWorkspaces = state.workspaces;
    },
  },
  extraReducers: (builder) => {
    // Fetch workspaces
    builder
      .addCase(fetchWorkspaces.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkspaces.fulfilled, (state, action: PayloadAction<Workspace[]>) => {
        state.loading = false;
        state.workspaces = action.payload;
        state.filteredWorkspaces = action.payload;
        if (!state.currentWorkspace && action.payload.length > 0) {
          state.currentWorkspace = action.payload[0];
        }
      })
      .addCase(fetchWorkspaces.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch workspaces';
      });

    // Fetch single workspace
    builder
      .addCase(fetchWorkspace.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkspace.fulfilled, (state, action: PayloadAction<Workspace>) => {
        state.loading = false;
        state.currentWorkspace = action.payload;
      })
      .addCase(fetchWorkspace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch workspace';
      });

    // Create workspace
    builder
      .addCase(createWorkspace.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWorkspace.fulfilled, (state, action: PayloadAction<Workspace>) => {
        state.loading = false;
        state.workspaces = state.workspaces || [];
        state.workspaces.push(action.payload);
        state.filteredWorkspaces = state.workspaces;
      })
      .addCase(createWorkspace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create workspace';
      });

    // Update workspace
    builder
      .addCase(updateWorkspace.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWorkspace.fulfilled, (state, action: PayloadAction<Workspace>) => {
        state.loading = false;
        const index = state.workspaces.findIndex(
          (workspace) => workspace.id === action.payload.id
        );
        if (index !== -1) {
          state.workspaces[index] = action.payload;
        }
        if (state.currentWorkspace?.id === action.payload.id) {
          state.currentWorkspace = action.payload;
        }
        state.filteredWorkspaces = state.workspaces;
      })
      .addCase(updateWorkspace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update workspace';
      });

    // Delete workspace
    builder
      .addCase(deleteWorkspace.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteWorkspace.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.workspaces = state.workspaces.filter(
          (workspace) => workspace.id !== action.payload
        );
        state.filteredWorkspaces = state.workspaces;
        if (state.currentWorkspace?.id === action.payload) {
          state.currentWorkspace = null;
          state.workspaceBoards = [];
        }
      })
      .addCase(deleteWorkspace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete workspace';
      });

    // Fetch workspace boards
    builder
      .addCase(fetchWorkspaceBoards.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkspaceBoards.fulfilled, (state, action: PayloadAction<Board[]>) => {
        state.loading = false;
        state.workspaceBoards = action.payload;
      })
      .addCase(fetchWorkspaceBoards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch workspace boards';
      });

    // Add member
    builder
      .addCase(addWorkspaceMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addWorkspaceMember.fulfilled, (state, action: PayloadAction<WorkspaceMember>) => {
        state.loading = false;
        if (state.currentWorkspace) {
          if (!state.currentWorkspace.members) state.currentWorkspace.members = [];
          state.currentWorkspace.members.push(action.payload);
          state.currentWorkspace.memberCount = (state.currentWorkspace.memberCount || 0) + 1;
        }
      })
      .addCase(addWorkspaceMember.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error.message || 'Failed to add member';
      });

    // Remove member
    builder
      .addCase(removeWorkspaceMember.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeWorkspaceMember.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        if (state.currentWorkspace) {
          if (!state.currentWorkspace.members) state.currentWorkspace.members = [];
          state.currentWorkspace.members = state.currentWorkspace.members.filter(
            (member) => member.userId !== action.payload
          );
          state.currentWorkspace.memberCount = Math.max(
            (state.currentWorkspace.memberCount || 0) - 1,
            0
          );
        }
      })
      .addCase(removeWorkspaceMember.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error.message || 'Failed to remove member';
      });

    // Update member role
    builder
      .addCase(updateMemberRole.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateMemberRole.fulfilled, (state, action: PayloadAction<WorkspaceMember>) => {
        state.loading = false;
        if (state.currentWorkspace) {
          if (!state.currentWorkspace.members) state.currentWorkspace.members = [];
          const index = state.currentWorkspace.members.findIndex(
            (member) => member.userId === action.payload.userId
          );
          if (index !== -1) {
            // The role-update response doesn't include the resolved `user`
            // (name/email/avatar) — merge onto the existing entry instead of
            // replacing it outright, so that display data isn't dropped.
            state.currentWorkspace.members[index] = {
              ...state.currentWorkspace.members[index],
              ...action.payload,
            };
          }
        }
      })
      .addCase(updateMemberRole.rejected, (state, action) => {
        state.loading = false;
        state.error = (action.payload as string) || action.error.message || 'Failed to update member role';
      });

    // Search workspaces
    builder
      .addCase(searchWorkspaces.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchWorkspaces.fulfilled, (state, action: PayloadAction<Workspace[]>) => {
        state.loading = false;
        state.filteredWorkspaces = action.payload;
      })
      .addCase(searchWorkspaces.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to search workspaces';
      });

    // Listen to board actions to update workspace boards
    builder
      .addCase(createBoard.fulfilled, (state, action: PayloadAction<Board>) => {
        state.workspaceBoards = state.workspaceBoards || [];
        state.workspaceBoards.push(action.payload);
        if (state.currentWorkspace) {
          state.currentWorkspace.boardCount = (state.currentWorkspace.boardCount || 0) + 1;
        }
      })
      .addCase(updateBoard.fulfilled, (state, action: PayloadAction<Board>) => {
        const index = state.workspaceBoards.findIndex((board) => board.id === action.payload.id);
        if (index !== -1) {
          state.workspaceBoards[index] = action.payload;
        }
      })
      .addCase(deleteBoard.fulfilled, (state, action: PayloadAction<string>) => {
        state.workspaceBoards = state.workspaceBoards.filter((board) => board.id !== action.payload);
        if (state.currentWorkspace) {
          state.currentWorkspace.boardCount = Math.max(
            (state.currentWorkspace.boardCount || 0) - 1,
            0
          );
        }
      });
  },
});

export const {
  setCurrentWorkspace,
  clearCurrentWorkspace,
  clearError,
  setSearchQuery,
  clearSearch,
} = workspaceSlice.actions;

export default workspaceSlice.reducer;
